import { useEffect, useReducer } from "react";
import { DateTime } from "luxon";

import { useWidget } from "/imports/state/WidgetContext";

const DEFAULT_TZ = "Asia/Dubai";

// A boundary further out than this isn't worth holding a timer for — the page
// will have been refetched long before. setTimeout also overflows past ~24.8
// days and would fire immediately.
const MAX_TIMER_MS = 24 * 60 * 60 * 1000;

// Now in the club's wall clock labelled Z — the frame the server sends window
// bounds in, and the one booking dates are stored in. Comparing a plain
// DateTime.now() against them is wrong by the club's offset (four hours in
// Dubai), which on a 15-minute window lands the button on the wrong side.
const nowFakeUTC = (clubZone) => DateTime.now().setZone(clubZone).setZone("UTC", { keepLocalTime: true });

/**
 * Where the booking sits relative to its check-in window right now:
 * "before" | "open" | "closed", or null when there is no window at all.
 *
 * The server sends `checkInWindow: { opensAt, closesAt } | null` — null means
 * the client isn't eligible for a reason that has nothing to do with the clock
 * (club has it off, wrong booking kind, seat cancelled, already checked in).
 * So this hook only ever answers the time question; it never re-derives the
 * eligibility rules, which would mean duplicating the server's guard.
 *
 * It re-renders exactly once per boundary rather than polling: one timer to the
 * moment the window opens, then one to the moment it closes.
 *
 * This governs rendering only. The server guard is untouched and still
 * authoritative — a device with a skewed clock shows the button early and gets
 * a 409 on the tap, which surfaces as the dialog's error message.
 */
export function useCheckInPhase(checkInWindow) {
  const { saas } = useWidget();
  const clubZone = saas?.timeZone || DEFAULT_TZ;
  const [, tick] = useReducer((n) => n + 1, 0);

  const opensAt = checkInWindow?.opensAt ? DateTime.fromISO(checkInWindow.opensAt, { zone: "utc" }) : null;
  const closesAt = checkInWindow?.closesAt ? DateTime.fromISO(checkInWindow.closesAt, { zone: "utc" }) : null;

  const now = nowFakeUTC(clubZone);
  const phase = !opensAt || !closesAt ? null : now < opensAt ? "before" : now <= closesAt ? "open" : "closed";

  useEffect(() => {
    if (!opensAt || !closesAt) return;

    // Before the window: wake when it opens. Inside it: wake when it closes.
    // Past it: nothing left to wait for.
    const next = now < opensAt ? opensAt : now <= closesAt ? closesAt : null;
    if (!next) return;

    // +1s so the timer lands just inside the new state rather than exactly on
    // the boundary, where a sub-second rounding difference could flip it back.
    const ms = next.diff(now).toMillis() + 1000;
    if (ms <= 0 || ms > MAX_TIMER_MS) return;

    const timer = setTimeout(tick, ms);
    return () => clearTimeout(timer);
  }, [checkInWindow?.opensAt, checkInWindow?.closesAt, phase, clubZone]); // eslint-disable-line react-hooks/exhaustive-deps

  return phase;
}

/** Whether the check-in button should be showing right now. */
export default function useCheckInOpen(checkInWindow) {
  return useCheckInPhase(checkInWindow) === "open";
}

/**
 * When a not-yet-open window opens, for surfaces that say so in words: "16:30",
 * "tomorrow 16:30", "12 Aug, 16:30". The day is named whenever it isn't today,
 * because a bare time on a booking dated next week reads as today's.
 *
 * opensAt is fake UTC — the club's wall clock labelled Z — so reading it as UTC
 * gives the club's own time without the browser's zone shifting it.
 */
export const checkInOpensLabel = (opensAt, clubZone) => {
  const opens = DateTime.fromISO(opensAt || "", { zone: "utc" });
  if (!opens.isValid) return null;

  const nowClub = DateTime.now().setZone(clubZone || DEFAULT_TZ);
  const opensDate = opens.toISODate();
  const day =
    opensDate === nowClub.toISODate() ? "" : opensDate === nowClub.plus({ days: 1 }).toISODate() ? "tomorrow " : `${opens.toFormat("d LLL")}, `;

  return `${day}${opens.toFormat("HH:mm")}`;
};
